import { useState, useEffect } from "react";
import SelfCancel from "./SelfCancel";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/karmivo/Navbar";
import StatusBadge, { AssignmentPill } from "@/components/karmivo/StatusBadge";
import { api } from "@/lib/api";
import { collectPayment } from "@/lib/razorpay";
import { Search, Check, Clock, CreditCard, Loader2 } from "lucide-react";

const TIMELINE = [
  { key: "created", label: "Booking Created" },
  { key: "paid", label: "Payment Successful" },
  { key: "finding", label: "AI Finding Workers" },
  { key: "assigned", label: "Workers Assigned" },
  { key: "progress", label: "Work In Progress" },
  { key: "completed", label: "Completed" },
];

function stageIndex(b) {
  if (!b) return -1;
  if (b.status === "completed") return 5;
  if (b.status === "in_progress") return 4;
  if (b.status === "fully_assigned" || b.status === "partially_assigned") return 3;
  if (b.status === "finding_workers") return 2;
  if (b.payment_status === "successful") return 1;
  return 0;
}

export default function Track() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const payBalance = async () => {
    if (!b) return;
    setPaying(true);
    try {
      const { data: order } = await api.post(`/payments/create-order`, null, { params: { booking_id: b.id, stage: "balance" } });
      const paid = await collectPayment(order, { description: `Balance for ${b.code || "booking"}`, prefill: { name: b.customer_name, contact: b.customer_phone } });
      await api.post(`/payments/verify`, { booking_id: b.id, ...paid, stage: "balance" });
      toast.success("Balance paid! Work Start Code sent to your mobile.");
      search();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setPaying(false); }
  };

  const search = async () => {
    if (!code && !phone) return toast.error("Enter booking code or phone");
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/track`, { params: { code: code || undefined, phone: phone || undefined } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Not found"); setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (code) search(); /* eslint-disable-next-line */ }, []);

  const b = data?.booking;
  const stage = stageIndex(b);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900">Track Your Booking</h1>
        <Card className="p-5 mt-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Booking Code</Label><Input value={code} onChange={e => setCode(e.target.value)} placeholder="KRM0000000" data-testid="track-code-input"/></div>
            <div><Label>Mobile Number</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Alternative" data-testid="track-phone-input"/></div>
            <div className="flex items-end"><Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={search} disabled={loading} data-testid="track-search-btn"><Search size={16} className="mr-2"/>Track</Button></div>
          </div>
        </Card>

        {b && (
          <Card className="mt-6 p-6" data-testid="track-result">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Booking</div>
                <div className="font-display text-2xl font-bold text-slate-900 font-mono">{b.code}</div>
                <div className="text-sm text-slate-600 mt-1">{b.pricing?.service_label || b.category} • {b.scheduled_date} • {b.start_time}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge value={b.payment_status} testid="track-payment-status"/>
                <AssignmentPill assigned={b.assigned_count || 0} needed={b.worker_count}/>
                <SelfCancel booking={b} onCancelled={search}/>
              </div>
            </div>

            {b.status === "cancelled" && (
              <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="track-cancelled-banner">
                This booking was cancelled{b.cancellation?.at ? ` on ${new Date(b.cancellation.at).toLocaleString("en-IN")}` : ""}.
                {b.cancellation && <> Cancellation charge: <b>{b.cancellation.fee ? `₹${b.cancellation.fee}` : "Free"}</b>{b.cancellation.refund_due > 0 && <> · refund of ₹{b.cancellation.refund_due} will be processed by our team</>}.</>}
              </div>
            )}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                {TIMELINE.map((t, i) => (
                  <div key={t.key} className="flex-1 flex flex-col items-center text-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${i <= stage ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                      {i < stage ? <Check size={16}/> : i === stage ? <Clock size={16} className="animate-pulse"/> : i + 1}
                    </div>
                    <div className={`text-[11px] mt-1.5 ${i <= stage ? "text-slate-900 font-semibold" : "text-slate-400"}`}>{t.label}</div>
                    {i < TIMELINE.length - 1 && <div className={`absolute h-0.5 ${i < stage ? "bg-emerald-500" : "bg-slate-200"}`}/>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <Card className="p-4 bg-slate-50 border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Work Site</div>
                <div className="mt-1 font-semibold text-slate-900">{b.location}</div>
                <div className="text-sm text-slate-600 mt-2">{b.work_description}</div>
              </Card>
              <Card className="p-4 bg-slate-50 border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Payment</div>
                <div className="mt-1 text-sm space-y-1">
                  <div className="flex justify-between"><span>Total booking</span><span className="font-mono font-bold">₹{b.amount}</span></div>
                  <div className="flex justify-between"><span>Advance paid</span><span className="font-mono text-emerald-600">₹{b.amount_paid ?? 0}</span></div>
                  <div className="flex justify-between font-bold"><span>Remaining</span><span className="font-mono text-orange-600" data-testid="track-remaining">₹{b.amount_due ?? 0}</span></div>
                </div>
                {b.balance_payment_status === "pending" && b.amount_due > 0 && (
                  <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={payBalance} disabled={paying} data-testid="pay-balance-btn">
                    {paying ? <Loader2 className="animate-spin mr-2" size={14}/> : <CreditCard className="mr-2" size={14}/>}
                    Pay Remaining ₹{b.amount_due}
                  </Button>
                )}
                {b.payment_status === "fully_paid" && <div className="mt-2 text-xs text-emerald-700 font-semibold">✓ Fully Paid · Work Start Code sent to mobile</div>}
              </Card>
            </div>

            <div className="mt-6">
              <div className="text-sm font-bold text-slate-900">Assigned Workers ({b.assigned_count || 0}/{b.worker_count})</div>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {(data.assignments || []).map(a => (
                  <Card key={a.id} className="p-3 flex items-center gap-3" data-testid={`assigned-worker-${a.worker_id}`}>
                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center">{a.worker_name?.[0] || "W"}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-slate-900">{a.worker_name}</div>
                      <div className="text-xs text-slate-500 font-mono">#{a.worker_id.slice(0,8)} • {a.status}</div>
                    </div>
                  </Card>
                ))}
                {(data.assignments || []).length === 0 && <div className="text-sm text-slate-500">AI dispatch in progress…</div>}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
